import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Popper from '@mui/material/Popper';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Field from './Field';
import { borderLabel, useLabelPlacement } from './labelPlacement';
import { formatTime, parseTime, toTimeOfDay, toTimeString, type TimeOfDay } from './dateUtils';
import { describedBy, inputAriaLabel, inputAttrProps, isInvalid, useFieldIds, type BaseFieldProps } from './fieldUtils';
import './form.css';

/**
 * Time field: typed "9:30 AM" entry plus a list of times at `interval`
 * minutes (DateBox type="time").
 *
 * Values: by default an "HH:mm" string ("HH:mm:ss" with `showSeconds`), 24-hour,
 * the shape <input type="time"> uses. `valueType="date"` takes and reports a
 * Date instead; the reported Date keeps the day of the current value (today
 * when there is none) with the new clock time. `value` also accepts
 * "yyyy-MM-ddTHH:mm:ss" (Settings' 1900-01-01 sentinel strings).
 *
 * onValueChanged fires only when a person changes the time (typing then Enter
 * or leaving the field, picking from the list, clearing), never when the
 * `value` prop changes.
 *
 * Typing accepts "9:30 AM", "930p", "9 pm", and 24-hour "21:30"; without AM
 * or PM the hour is read on a 24-hour clock. Anything else is kept and flagged.
 *
 * Keyboard: Alt+Down or the clock button opens the list on the current time;
 * Up/Down, PageUp/PageDown, Home/End move, Enter picks, Escape closes, and
 * focus returns to the field.
 */
interface TimeFieldCommon extends BaseFieldProps {
  value: string | Date | null | undefined;
  /** Minutes between the times in the list (DateBox `interval`). Default 15. */
  interval?: number;
  /** Show and keep seconds ("9:30:15 AM", DateBox displayFormat "h:mm:ss a"). */
  showSeconds?: boolean;
  /** Show a clear button while there is a time. Off by default, as DateBox. */
  showClearButton?: boolean;
}

interface TimeFieldString extends TimeFieldCommon {
  valueType?: 'string';
  /** "HH:mm" ("HH:mm:ss" with showSeconds), or null when cleared. User changes only. */
  onValueChanged: (value: string | null) => void;
}

interface TimeFieldDate extends TimeFieldCommon {
  valueType: 'date';
  /** The value's day (or today) at the new time, or null when cleared. User changes only. */
  onValueChanged: (value: Date | null) => void;
}

export type TimeFieldProps = TimeFieldString | TimeFieldDate;

export default function TimeField(props: TimeFieldProps) {
  const {
    value, label, ariaLabel, required, disabled, readOnly, helperText, errorText,
    width, height, className, inputAttr, interval = 15, showSeconds = false, showClearButton = false, labelPlacement,
  } = props;
  const placeholder = props.placeholder ?? (showSeconds ? 'h:mm:ss AM' : 'h:mm AM');
  const ids = useFieldIds(inputAttr);
  const time = toTimeOfDay(value);
  const key = time == null ? '' : String(time);

  const [draft, setDraft] = useState<{ text: string; forKey: string; error?: string } | null>(null);
  const live = draft && draft.forKey === key ? draft : null;
  const text = live ? live.text : time != null ? formatTime(time, showSeconds) : '';
  const typedError = live?.error;

  const step = Math.max(1, Math.round(interval));
  const options: TimeOfDay[] = Array.from({ length: Math.ceil(1440 / step) }, (_, i) => i * step * 60);

  const [open, setOpen] = useState(false);
  const [listWidth, setListWidth] = useState<number | undefined>(undefined);
  const [active, setActive] = useState(0);
  const anchorRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const popupId = `${ids.id}-times`;
  const editable = !disabled && !readOnly;

  const emit = (t: TimeOfDay | null) => {
    if (props.valueType === 'date') {
      if (t == null) { props.onValueChanged(null); return; }
      const base = value instanceof Date && !Number.isNaN(value.getTime()) ? value : new Date();
      props.onValueChanged(new Date(base.getFullYear(), base.getMonth(), base.getDate(), Math.floor(t / 3600), Math.floor((t % 3600) / 60), t % 60));
    } else {
      props.onValueChanged(t == null ? null : toTimeString(t, showSeconds));
    }
  };

  const commit = () => {
    if (!live) return;
    const t = live.text.trim();
    if (!t) {
      setDraft(null);
      if (time != null) emit(null);
      return;
    }
    let parsed = parseTime(t);
    if (parsed == null) { setDraft({ ...live, error: 'Enter a time as h:mm AM or PM' }); return; }
    if (!showSeconds) parsed -= parsed % 60;
    setDraft(null);
    if (parsed !== time) emit(parsed);
  };

  // The option nearest the current time (the one at or before it).
  const nearest = () => (time == null ? Math.min(options.length - 1, Math.floor((8 * 60) / step)) : Math.min(options.length - 1, Math.floor(time / 60 / step)));

  const openList = () => {
    if (!editable || open) return;
    commit();
    setActive(nearest());
    setListWidth(anchorRef.current?.offsetWidth);
    setOpen(true);
  };

  const closeList = (refocus: boolean) => {
    if (!open) return;
    setOpen(false);
    if (refocus) inputRef.current?.focus();
  };

  const pick = (t: TimeOfDay) => {
    setDraft(null);
    if (t !== time) emit(t);
    closeList(true);
  };

  const scrollToActive = (list: HTMLUListElement | null, index: number, block: ScrollLogicalPosition) => {
    const el = list?.querySelector<HTMLElement>(`[data-index="${index}"]`);
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block });
  };
  // The popper mounts its content a render after `open`, so focus the list
  // when the element itself arrives, with the active time in the middle (the
  // list already carries it as aria-activedescendant).
  const setList = useCallback((el: HTMLUListElement | null) => {
    listRef.current = el;
    if (!el) return;
    el.focus();
    const activeId = el.getAttribute('aria-activedescendant');
    const activeEl = activeId ? el.querySelector<HTMLElement>(`[id="${activeId}"]`) : null;
    if (activeEl && typeof activeEl.scrollIntoView === 'function') activeEl.scrollIntoView({ block: 'center' });
  }, []);

  // Keep the active option in view as the keyboard moves it.
  useLayoutEffect(() => {
    if (open) scrollToActive(listRef.current, active, 'nearest');
  }, [open, active]);

  const onInputKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.altKey && e.key === 'ArrowDown') { e.preventDefault(); openList(); }
    else if (e.key === 'Enter') {
      commit();
      // Enter here only accepts the typed time: it never also reaches a dialog's
      // Enter = Save, which could run before the new time is committed and save the
      // old one. Ctrl+Enter still saves (useDialogKeys waits a tick for the commit).
      if (editable && !e.ctrlKey && !e.metaKey) { e.preventDefault(); e.stopPropagation(); }
    }
  };

  const onListKeyDown = (e: KeyboardEvent<HTMLUListElement>) => {
    const last = options.length - 1;
    const page = Math.max(1, Math.round(60 / step));
    let next: number | null = null;
    if (e.key === 'ArrowDown') next = Math.min(last, active + 1);
    else if (e.key === 'ArrowUp') next = Math.max(0, active - 1);
    else if (e.key === 'PageDown') next = Math.min(last, active + page);
    else if (e.key === 'PageUp') next = Math.max(0, active - page);
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = last;
    else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pick(options[active]); return; }
    else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); closeList(true); return; }
    else if (e.key === 'Tab') { closeList(false); return; }
    if (next != null) { e.preventDefault(); setActive(next); }
  };

  const invalid = isInvalid(props) || !!typedError;
  const shownError = errorText ?? typedError;
  const name = label ?? ariaLabel ?? 'time';
  const placement = useLabelPlacement(labelPlacement);
  const outlineLabel = borderLabel(placement, label, required);
  const container = () => anchorRef.current?.closest<HTMLElement>('.MuiDialog-root, .MuiModal-root') ?? document.body;
  const selectedIndex = time != null && time % (step * 60) === 0 ? time / 60 / step : -1;

  return (
    <Field
      inputId={ids.id}
      label={label}
      required={required}
      helperText={helperText}
      helperId={ids.helperId}
      errorText={shownError}
      errorId={ids.errorId}
      error={invalid}
      disabled={disabled}
      width={width}
      height={height}
      labelPlacement={placement}
      className={`${readOnly ? 'app-field--readonly ' : ''}${className ?? ''}`.trim() || undefined}
    >
      <TextField
        id={ids.id}
        className="app-time-input"
        value={text}
        onChange={(e) => setDraft({ text: e.target.value, forKey: key })}
        onBlur={commit}
        onKeyDown={onInputKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        error={invalid}
        size="small"
        fullWidth
        label={outlineLabel}
        InputLabelProps={outlineLabel ? { shrink: true } : undefined}
        inputRef={inputRef}
        InputProps={{
          ref: anchorRef,
          notched: !!outlineLabel,
          readOnly,
          endAdornment: (
            <InputAdornment position="end">
              {showClearButton && editable && time != null && (
                <IconButton size="small" aria-label={`Clear ${name}`} onClick={() => { setDraft(null); emit(null); }} edge={false}>
                  <i className="bi bi-x-lg" aria-hidden="true" style={{ fontSize: 12 }} />
                </IconButton>
              )}
              <IconButton
                size="small"
                edge="end"
                aria-label={`Choose time${label ?? ariaLabel ? `, ${label ?? ariaLabel}` : ''}`}
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-controls={open ? popupId : undefined}
                disabled={!editable}
                onClick={() => (open ? closeList(true) : openList())}
              >
                <i className="bi bi-clock" aria-hidden="true" style={{ fontSize: 14 }} />
              </IconButton>
            </InputAdornment>
          ),
        }}
        inputProps={{
          ...inputAttrProps(inputAttr),
          autoComplete: 'off',
          'aria-label': inputAriaLabel(label, required, ariaLabel),
          'aria-required': required || undefined,
          'aria-invalid': invalid || undefined,
          'aria-describedby': describedBy(ids, { helperText, errorText: shownError }),
        }}
      />
      <Popper open={open} anchorEl={() => anchorRef.current!} placement="bottom-start" className="app-time-popup" container={container}>
        <ClickAwayListener onClickAway={(e) => { if (!anchorRef.current?.contains(e.target as Node)) closeList(false); }}>
          <ul
            id={popupId}
            ref={setList}
            role="listbox"
            aria-label={`Choose ${name}`}
            tabIndex={-1}
            className="app-time-list"
            aria-activedescendant={`${popupId}-${active}`}
            onKeyDown={onListKeyDown}
            style={{ minWidth: listWidth }}
          >
            {options.map((t, i) => (
              <li
                key={t}
                id={`${popupId}-${i}`}
                data-index={i}
                role="option"
                aria-selected={i === selectedIndex}
                className={`app-time-option${i === active ? ' app-time-option--active' : ''}${i === selectedIndex ? ' app-time-option--selected' : ''}`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(t)}
              >
                {formatTime(t, false)}
              </li>
            ))}
          </ul>
        </ClickAwayListener>
      </Popper>
    </Field>
  );
}
